/**
 * Vendor Domain Types - Frontend
 *
 * Single source of truth for vendor-related types on the frontend.
 * No axios, no React, no framework - pure TypeScript.
 */

export type AuthType = 'none' | 'api-key' | 'bearer' | 'basic' | 'oauth-client-credentials';
export type RequestBodyType = 'json' | 'form-urlencoded';
export type DoctrineId = 'IDENTITY_VERIFICATION' | 'EMPLOYER_VERIFICATION' | 'INCOME_VERIFICATION';
export type ServiceName =
  | 'identity-verification'
  | 'income-verification'
  | 'employer-verification'
  | 'ai-gateway'
  | 'decision-engine';

/** Slug accepted by GET/PUT /vendors/service-routing/:serviceName */
export type RoutingServiceKey = string;

export const ROUTING_SERVICE_KEY_PATTERN = /^[a-z0-9_-]+$/;

export function isRoutingServiceKey(value: string): value is RoutingServiceKey {
  return ROUTING_SERVICE_KEY_PATTERN.test(value.trim());
}

/** Well-known keys for labels/autocomplete; catalog may add more at runtime */
export const KNOWN_ROUTING_SERVICE_KEYS = [
  'income-verification',
  'identity-verification',
  'employer-verification',
  'ai-gateway',
  'decision-engine',
] as const;

export interface FieldMapping {
  canonicalPath: string;
  vendorPath: string;
  required: boolean;
  transform?: string;
}

export type EndpointOnboardingStatus = 'draft' | 'complete';

export interface VendorEndpointMapping {
  serviceName?: ServiceName | string;
  capability: string;
  /** Per-API auth overrides (Connect step); merged over vendor authConfig at sync/runtime. */
  authConfig?: Record<string, string>;
  endpointTemplate?: string;
  requestMethod: string;
  requestPath: string;
  requestHeaders: Record<string, string>;
  requestBodyType?: RequestBodyType;
  requestBody?: unknown | null;
  requestMapping?: string;
  priority?: number;
  enabled?: boolean;
  /** draft = in-progress add-api wizard; complete or missing = published API. */
  onboardingStatus?: EndpointOnboardingStatus;
  /** Wizard step (0–3) to reopen when onboardingStatus is draft. */
  onboardingStep?: number;
  responseFields: string[];
  fieldMappings?: FieldMapping[];
  sampleResponse?: unknown | null;
  normalizedPreview?: unknown | null;
  responseMapping: Record<string, string>;
  testUrl?: string;
  testMethod?: string;
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  bodyFormat?: RequestBodyType;
}

export interface VendorServiceRoutingVendor {
  vendorId: string;
  priority: number;
  enabled: boolean;
}

export interface VendorServiceRouting {
  serviceName: RoutingServiceKey;
  vendors: VendorServiceRoutingVendor[];
  /** Adapter DB label used when auto-sync runs after save (defaults to server env label). */
  adapterConfigLabel?: string;
}

export interface AdapterConfigRoutingService {
  serviceKey: RoutingServiceKey;
  vendors: VendorServiceRoutingVendor[];
}

export interface AdapterConfigRoutingSnapshot {
  label: string;
  services: AdapterConfigRoutingService[];
}

export interface AdapterConfigRoutingUpdateResponse {
  label: string;
  serviceKey: RoutingServiceKey;
  vendors: VendorServiceRoutingVendor[];
}

/** Present on vendor mutation responses when admin-api re-exports vendorMappings.json for the adapter. */
export type VendorMappingsExportResult = { ok: true } | { ok: false; error: string };
export type ThirdPartyConfigSyncResult =
  | { ok: true; label: string; configKey: string; vendorCount: number; syncedAt: string }
  | { ok: false; label: string; configKey: string; vendorCount?: number; attemptedAt: string; error: string };

export interface ThirdPartyConfigSyncStatus {
  id: string;
  label: string;
  configKey: string;
  status: 'success' | 'failed' | 'never';
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  error: string | null;
  vendorCount: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type VendorMappingSaveResponse = VendorMapping & {
  vendorMappingsExport?: VendorMappingsExportResult;
  thirdPartyConfigSync?: ThirdPartyConfigSyncResult;
};

export type VendorServiceRoutingSaveResponse = VendorServiceRouting & {
  vendorMappingsExport?: VendorMappingsExportResult;
  thirdPartyConfigSync?: ThirdPartyConfigSyncResult;
};

export interface VendorDeleteResult {
  vendorMappingsExport: VendorMappingsExportResult;
  thirdPartyConfigSync?: ThirdPartyConfigSyncResult;
}

export interface VendorMapping {
  vendorId: string;
  vendorName?: string;
  // step 1 - connection
  baseUrl: string;
  authType: AuthType;
  authConfig: Record<string, string>; // { headerName, apiKey } | { token } | { username, password }
  /** Vendor-wide variables (model, etc.) — synced to adapter provider row and JSONata `c`. */
  providerVariables?: Record<string, string>;
  doctrineId?: DoctrineId;
  /** Published adapter_configs.label chosen at vendor onboarding. */
  adapterConfigLabel?: string;
  /** adapterServiceKey:configLabel group chosen at vendor onboarding. */
  preferredCatalogGroupKey?: string;
  serviceName?: ServiceName | string;
  capability?: string;
  endpointTemplate?: string;
  // step 2 - request
  requestMethod: string;
  requestPath: string;
  requestHeaders: Record<string, string>;
  requestBodyType?: RequestBodyType;
  requestBody?: unknown | null;
  requestMapping?: string;
  // step 3/4 - response field selection
  responseFields: string[]; // dot-notation leaf paths chosen by admin
  fieldMappings?: FieldMapping[];
  sampleResponse?: unknown | null;
  normalizedPreview?: unknown | null;
  responseMapping?: Record<string, string>;
  endpointMappings?: VendorEndpointMapping[];
  // legacy - kept so existing saved vendors still render without error
  payload: Record<string, unknown>;
  testUrl?: string;
  testMethod?: string;
}

export interface TestResult {
  statusCode: number;
  statusText: string;
  ok: boolean;
  durationMs: number;
  error?: string;
}

export interface LiveTestRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: unknown;
  bodyType?: RequestBodyType;
  /** Selected vendor capability, used to decide endpoint-specific managed auth behavior. */
  capability?: string;
  /** When provided, the backend auto-fetches an OAuth token for this vendor before the live test. */
  vendorId?: string;
  /** Draft auth settings from the onboarding wizard, used before the vendor has been saved. */
  authDraft?: {
    baseUrl: string;
    authType: AuthType;
    authConfig: Record<string, string>;
  };
}

export interface LiveTestResult extends TestResult {
  responseBody: unknown; // parsed JSON or raw string from vendor
  oauth?: {
    enabled: boolean;
    attempted: boolean;
    tokenUrl?: string;
    requestedScope?: string;
    tokenStatusCode?: number;
    tokenStatusText?: string;
    tokenOk?: boolean;
    tokenInjected: boolean;
    error?: string;
  };
  preflight?: {
    enabled: boolean;
    attempted: boolean;
    tokenUrl?: string;
    tokenStatusCode?: number;
    tokenStatusText?: string;
    tokenOk?: boolean;
    tokenInjected: boolean;
    targetHeaderName?: string;
    tokenResponsePath?: string;
    error?: string;
  };
  auth?: {
    attempted: boolean;
    injected: boolean;
    error?: string;
  };
}

export interface CreateVendorRequest {
  vendorId: string;
  vendorName?: string;
  /** adapter_configs.label used when syncing vendor mappings after save */
  adapterConfigLabel?: string;
  /** adapterServiceKey:configLabel group chosen at vendor onboarding. */
  preferredCatalogGroupKey?: string;
  baseUrl: string;
  authType: AuthType;
  authConfig: Record<string, string>;
  providerVariables?: Record<string, string>;
  doctrineId?: DoctrineId;
  serviceName?: ServiceName | string;
  capability?: string;
  endpointTemplate?: string;
  requestMethod: string;
  requestPath: string;
  requestHeaders: Record<string, string>;
  requestBodyType?: RequestBodyType;
  requestBody?: unknown | null;
  requestMapping?: string;
  responseFields: string[];
  fieldMappings?: FieldMapping[];
  sampleResponse?: unknown | null;
  normalizedPreview?: unknown | null;
  responseMapping?: Record<string, string>;
  endpointMappings?: VendorEndpointMapping[];
  payload: Record<string, unknown>;
  testUrl?: string;
  testMethod?: string;
}

export interface UpdateVendorRequest {
  vendorName?: string;
  baseUrl?: string;
  authType?: AuthType;
  authConfig?: Record<string, string>;
  providerVariables?: Record<string, string>;
  doctrineId?: DoctrineId;
  /** Published adapter_configs.label chosen at vendor onboarding. */
  adapterConfigLabel?: string;
  /** adapterServiceKey:configLabel group chosen at vendor onboarding. */
  preferredCatalogGroupKey?: string;
  serviceName?: ServiceName | string;
  capability?: string;
  endpointTemplate?: string;
  requestMethod?: string;
  requestPath?: string;
  requestHeaders?: Record<string, string>;
  requestBodyType?: RequestBodyType;
  requestBody?: unknown | null;
  requestMapping?: string;
  responseFields?: string[];
  fieldMappings?: FieldMapping[];
  sampleResponse?: unknown | null;
  normalizedPreview?: unknown | null;
  responseMapping?: Record<string, string>;
  endpointMappings?: VendorEndpointMapping[];
  payload?: Record<string, unknown>;
  testUrl?: string;
  testMethod?: string;
}

export interface TestConnectionRequest {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  /** When provided for OAuth vendors, the backend tests the token endpoint instead of probing the base URL. */
  vendorId?: string;
  authDraft?: {
    baseUrl: string;
    authType: AuthType;
    authConfig: Record<string, string>;
  };
}
